import { IconButton as MuiIconButton, IconButtonProps as MuiIconButtonProps } from '@mui/material';
import { forwardRef } from 'react';

export interface IconButtonProps extends Omit<MuiIconButtonProps, 'color'> {
  /**
   * The color of the component.
   * @default 'default'
   */
  color?:
    | 'inherit'
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * If `true`, the button will be disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * The content of the button (typically an icon).
   */
  children: React.ReactNode;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: MuiIconButtonProps['sx'];
  /**
   * Callback fired when the button is clicked.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * When set, renders as an `<a>` instead of a `<button>`.
   */
  href?: string;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: string;
  /**
   * The title attribute for the button (tooltip).
   */
  title?: string;
  /**
   * The aria-label attribute for accessibility.
   */
  'aria-label'?: string;
  /**
   * The data-testid attribute for testing.
   */
  'data-testid'?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      color = 'default',
      size = 'medium',
      disabled = false,
      children,
      sx,
      onClick,
      title,
      'aria-label': ariaLabel,
      'data-testid': dataTestId,
      ...props
    },
    ref
  ) => (
    <MuiIconButton
      ref={ref}
      color={color}
      size={size}
      disabled={disabled}
      sx={sx}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      {...props}
    >
      {children}
    </MuiIconButton>
  )
);

IconButton.displayName = 'IconButton';
